/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAddDynamicFilterPayload,
  IAutoDrilldownPayload,
  IAutoDrilldownV2Data,
  ICustomHierarchyDrilldownPayload,
  IDeleteDynamicFilterPayload,
  IDrilldownChangeForCustomHierarchyPayload,
  IDrilldownChangePayload,
  IFilterChangePayload,
  ILoadMoreDropdownOptionsPayload,
  IPaginationChangePayload,
  IReorderMeasurePayload,
  IReverseDrilldownPayload,
  ISearchDropdownOptionsPayload,
  ISelectedToolbarMenuItemPayload,
  ISetBinningPayload,
  ISetChartDataPayload,
  ISetGroupingDrilldownPayload,
  ISetGroupingPayload,
  ISetMeasureStatePayload,
  ISetReportInfoPayload,
  ISetShowReportPayload,
  ISetVisualizationPayload,
  ISetZeroAnchorPayload,
  ISortingChangePayload,
  IUpdateMeasurePayload,
  IUpdateTriggerExportPayload,
} from 'core/models/report-redux';
import { APIRequestStatus } from 'core/constants/redux';
import SieraResponse from 'core/models/siera-response';
import {
  DynamicMeasureResponse, IColumn, IDataRefreshStatus, IExportData, IExportUsageData, IField, IFilterConfig, IReportConfig, IReportData,
  ISetDimensionsEntityGroupingPayload,
  ISetEntityGroupingPayload,
  ISetMetaSelectorOptionsPayload,
} from 'core/models/report-response';
import { ObjModel } from 'core/models';
import { Auth } from 'core/models/auth-response';
import { IHierarchy, SortingOrderTypes } from 'core/models/report-builder/report-builder';
import {
  getAppliedMeasures, getDefaultEntity, getIsReportBuilderSaveFormDisable, sortColumns,
} from 'core/utils/report.util';
import {

  ColumnModes, DataRefreshEntityTypes, DimensionMode, DimensionStatus, ExportStatus,
} from 'core/constants/report';
import {
  ChartDefaultcolors, ComboAxisAlignment, ComboDisplayType, VisualizationTypes,
} from 'core/constants/visualizations';
import {
  buildCustomFiltersFromDefinitions,
  getSaveSaveAsURL,
  resolveSubBuilderCustomFilterDefaults,
} from 'core/utils/common.utility';
import { isValidArray, isValidArrayAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import FilterFetchUtility from 'core/utils/filter-fetch-utility';
import { FilterType, UserFilterPayloadType } from 'core/constants/filter';
import { getEntityJoinIdForMeasure } from 'components/feature/ReportBuilderV2/ReportBuilderDetail/DrilldownTabContent/drilldown.util';

import { isListReport } from 'core/utils/report-builder.util';
import { IConditionalFormatting, ILinkedConfig, IPreviewDataError } from 'redux-v2/report-builder/report-builder-store.state';
import reportInitialState from './report-store.initial-state';
import {
  accesscontrol,
  deleteReport,
  getDataRefreshDetails,
  getDataRefreshStatus,
  getDynamicDimensions,
  getDynamicFilters,
  getDynamicMeasures,
  getExportData,
  getExportDataV2,
  getExportListDataByIds,
  getExportLists,
  getExportUsageStatus,
  getExpressionFields,
  getFetchDrilldownReportV2,
  getFiltersLoad,
  getRawConfig, getReportConfig, getReportData, getRetryExportDatagetRetryExportData, saveReportConfig,
  saveLinkedInsightDrilldown,
} from './report-store.requests';
import { IReportState, ReportActionTypes } from './report-store.state';
import {
  dataRefreshDetailsLoadSuccess, dataRefreshStatusLoadSuccess, getDynamicDimensionOnDynamicDimensionLoadSuccess, getSort, getStateOnAddDynamicFilter,
  getStateOnApplyDimensions, getStateOnApplyEntityGrouping, getStateOnApplyingFilters, getStateOnAutodrilldownReportDataLoadSuccess, getStateOnDeleteDynamicFilter,
  getStateOnDrilldownChange, getStateOnFilterChange, getStateOnFilterLoadFailure, getStateOnFilterLoadRequest, getStateOnFilterLoadSuccess,
  getStateOnLoadMoreDropdownOptions, getStateOnMeasureReorder, getStateOnMeasureSetState, getStateOnMeasureUpdate, getStateOnPaginationChange,
  getStateOnReorderColumns, getStateOnReportConfigLoadSuccess, getStateOnReportDataLoadSuccess, getStateOnReverseDrilldown, getStateOnSearchDropdownOptions,
  getStateOnSetBinning, getStateOnSetEntityGrouping, getStateOnSetEntityGroupingOptions, getStateOnSetGrouping, getStateOnSetGroupingDrilldown, getStateOnSetToolbarMenuItem, getStateOnSetZeroAnchor, getStateOnSortColumn,
  getStateOnToggleZeroAnchor, updateDynmaicMeasureStateOnSuccess,
  getStateOnCustomHierarchyDrilldownChange, getStateOnReverseCustomHierarchyDrilldown,
} from './report-store.utils';
import { calculateCustomHierarchyDrilldownColumnsFromHierarchies } from '../custom-hierarchy/custom-hierarchy.util';

const reportStoreSlice = createSlice({
  name: 'report',
  initialState: reportInitialState,
  reducers: {
    [ReportActionTypes.AUTODRILLDOWN]: (state:IReportState, action: PayloadAction<IAutoDrilldownPayload>) => {
      const {
        selectedMeasure, selectedRowData, measureFieldAlias, rowPinnedClicked, clickedOnColumnTotal, clickedOnRowTotal, clickedOnSubTotal, clickedOnGroupedSubTotal,
      } = action.payload as IAutoDrilldownPayload;

      // Use customHierarchyDrilldownData.columns if in custom hierarchy drilldown state
      // Otherwise use regular state.columns
      const columnsToSearch = isValidArrayAndNotEmpty(state.customHierarchyDrilldownData?.columns)
        ? state.customHierarchyDrilldownData.columns
        : state.columns;

      const selectedColumn = isValidArrayAndNotEmpty(columnsToSearch) && columnsToSearch.find((column:IColumn) => column.Id === selectedMeasure);
      const measureField = selectedMeasure ? getEntityJoinIdForMeasure(state.fields, selectedColumn, getDefaultEntity(state?.savedReportConfig)) : null;
      if (selectedMeasure) {
        state.autodrilldownData = {
          selectedMeasure,
          selectedRowData,
          page: {
            PageSize: 25,
            PageIndex: 1,
          },
          columns: [],
          sort: [],
          isMultiSort: false,
          reportData: null,
          drilldownFields: null,
          measureFieldAlias,
          rowPinnedClicked,
          clickedOnColumnTotal,
          clickedOnRowTotal,
          clickedOnGroupedSubTotal,
          clickedOnSubTotal,
          selectedMeasureInfo: measureField,
        };
      } else {
        state.autodrilldownData = null;
        state.autoDrilldownStateID = '';
        state.autoDrilldownDataV2 = null;
        if (isValidArrayAndNotEmpty(state.dynamicDimensions)) {
          state.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_DIMENSIONS_LOAD_REQUEST] = APIRequestStatus.Success;
        }
      }
    },
    [ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_REQUEST]: (state:IReportState) => {
      state.requestProcessing[ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Processing;
      if (!state?.autodrilldownData?.drilldownFields) state.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_DIMENSIONS_LOAD_REQUEST] = APIRequestStatus.Processing;
    },
    [ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_SUCCESS]: (state:IReportState, action: PayloadAction<IReportData>) => (getStateOnAutodrilldownReportDataLoadSuccess(state, action.payload)),
    [ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_FAILURE]: (state:IReportState, action: PayloadAction<any>) => {
      const errorData = action.payload;
      if (errorData?.ErrorMessage) {
        state.error = { ErrorMessage: errorData.ErrorMessage };
      }
      state.requestProcessing[ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Failure;
    },
    [ReportActionTypes.AUTODRILLDOWN_V2]: (state:IReportState, action: PayloadAction<IAutoDrilldownV2Data>) => {
      state.autoDrilldownDataV2 = action.payload;
      state.showReport = true;
      state.autoDrilldownStateID = action?.payload?.SourceId;

      // Dashboard drilldown ships custom-filter values keyed by the wrapper ID. Write
      // them onto the wrapper's appliedFilters/activeFilters so the filter sidebar shows
      // the dashboard selection from first render, and stash them so getRawConfig.fulfilled
      // can restore them after its schema-correcting re-init clobbers appliedFilters
      // (see drilldownWrittenWrapperFilters).
      //
      // The dashboard ships the RAW value (User → bare UserIds array; others →
      // FilterResponse). appliedFilters[].FilterResponse needs the ENVELOPED shape, so
      // re-wrap User wrappers using the select-type from FiltersMetadata
      // (getLinkedUserFilterType); other types' FilterResponse is the raw value as-is.
      const drilldownFilters = action?.payload?.Filters;
      if (drilldownFilters && Object.keys(drilldownFilters).length > 0 && isValidArrayAndNotEmpty(state.filterConfig)) {
        const userSelectType = action?.payload?.FiltersMetadata?.UserDropdownSelectType;
        const written: ObjModel.Obj = {};
        Object.keys(drilldownFilters).forEach((id) => {
          const wrapper = state.filterConfig.find((f) => f.ID === id && f.IsCustomFilter);
          if (!wrapper) return;
          const rawValue = drilldownFilters[id];
          const value = wrapper.Type === FilterType.UserMultiSelect
            ? {
              Type: userSelectType || wrapper?.DropdownMetaData?.DropdownSelectType || UserFilterPayloadType.All,
              UserIds: rawValue,
            }
            : rawValue;
          written[id] = value;
          if (state.appliedFilters?.[id]) state.appliedFilters[id].FilterResponse = value;
          if (state.activeFilters?.[id]) state.activeFilters[id].FilterResponse = value;
        });
        state.drilldownWrittenWrapperFilters = Object.keys(written).length > 0 ? written : undefined;
      }
    },
    [ReportActionTypes.APPLY_FILTERS]: (state:IReportState) => getStateOnApplyingFilters(state),
    [ReportActionTypes.FILTER_CHANGE]: (state: IReportState, action: PayloadAction<IFilterChangePayload>) => {
      const {
        filterId, filterValue, variables, useChangedFilters,
      } = action.payload as IFilterChangePayload;
      return getStateOnFilterChange(
        state,
        filterId,
        filterValue,
        variables,
        useChangedFilters,
      );
    },
    [ReportActionTypes.REORDER_COLUMNS]: (state: IReportState, action: PayloadAction<{columns: IColumn[], isAutodrilldown: boolean}>) => {
      const { columns, isAutodrilldown } = action.payload;
      return getStateOnReorderColumns(state, columns, isAutodrilldown);
    },
    [ReportActionTypes.PAGINATION_CHANGE]: (state:IReportState, action: PayloadAction<IPaginationChangePayload>) => {
      const {
        paginationKey, paginationValue, isAutodrilldown,
      } = action.payload as IPaginationChangePayload;
      return getStateOnPaginationChange(
        state,
        paginationKey,
        paginationValue,
        isAutodrilldown,
      );
    },

    [ReportActionTypes.SORTING_CHANGE]: (state:IReportState, action: PayloadAction<ISortingChangePayload>) => {
      const {
        columnType,
        columnName,
        order,
        multiSort,
        Id,
      } = action.payload as ISortingChangePayload;
      return getStateOnSortColumn(
        state,
        columnType,
        columnName,
        order,
        multiSort,
        Id,
      );
    },
    [ReportActionTypes.DRILLDOWN_SORTING_CHANGE]: (state:IReportState, action: PayloadAction<ISortingChangePayload>) => {
      const {
        columnType,
        columnName,
        order,
        multiSort,
        Id,
      } = action.payload as ISortingChangePayload;
      const drilldownFields = state?.autodrilldownData?.drilldownFields;
      const [sortState, hasChanged] = sortColumns(
        state.autodrilldownData.sort || [],
        state.activeDimensions,
        getAppliedMeasures(state.columns),
        columnType,
        columnName,
        order,
        !state.reportConfig.IsAugmented && multiSort,
        Id,
        drilldownFields,
        true,
      );
      if (!hasChanged) return;
      state.autodrilldownData.sort = sortState;
    },
    [ReportActionTypes.CHANGE_PIVOT_SORTING]: (state: IReportState, action: PayloadAction<SortingOrderTypes>) => {
      state.pivotSort = action.payload;
    },
    [ReportActionTypes.DRILLDOWN_CHANGE]: (state: IReportState, action: PayloadAction<IDrilldownChangePayload>) => {
      const {
        filterId, drilldownValue, user, variables, value,
      } = action.payload as IDrilldownChangePayload;
      return getStateOnDrilldownChange(
        state,
        filterId,
        drilldownValue,
        user,
        variables,
        value,
      );
    },
    [ReportActionTypes.DRILLDOWN_CHANGE_FOR_CUSTOM_HIERARCHY]: (state: IReportState, action: PayloadAction<IDrilldownChangeForCustomHierarchyPayload>) => {
      const {
        filterId, drilldownValue, user, variables, value, customHierarchyId, selectedColumnId,
      } = action.payload as IDrilldownChangeForCustomHierarchyPayload;

      return getStateOnCustomHierarchyDrilldownChange(
        state,
        filterId,
        drilldownValue,
        user,
        variables,
        value,
        customHierarchyId,
        selectedColumnId,
        value, // displayValue - could format based on column type
      );
    },
    [ReportActionTypes.REVERSE_DRILLDOWN]: (state: IReportState, action: PayloadAction<IReverseDrilldownPayload>) => {
      const { index } = action.payload as IReverseDrilldownPayload;
      // Check if this is custom hierarchy drilldown
      if (state.drilldownPoint?.customHierarchyId && state.customHierarchyDrilldownData) {
        return getStateOnReverseCustomHierarchyDrilldown(state, index);
      }

      // Regular user drilldown
      return getStateOnReverseDrilldown(state, index);
    },
    [ReportActionTypes.SET_TOOLBAR_MENU_ITEM]: (state:IReportState, action: PayloadAction<ISelectedToolbarMenuItemPayload>) => {
      const {
        index,
      } = action.payload as ISelectedToolbarMenuItemPayload;
      return getStateOnSetToolbarMenuItem(
        state,
        index,
      );
    },
    [ReportActionTypes.RESET_SHOW_OTHERS_COLUMN_FLAG]: (state: IReportState) => {
      state.showOthersInfoPopUp = false;
    },
    [ReportActionTypes.SET_GROUPING]: (state:IReportState, action: PayloadAction<ISetGroupingPayload>) => {
      const {
        sourceIndex, status, mode, destinationIndex, isSwapEnabled,
      } = action.payload as ISetGroupingPayload;
      return getStateOnSetGrouping(
        state,
        sourceIndex,
        status,
        mode,
        destinationIndex,
        isSwapEnabled,
      );
    },
    [ReportActionTypes.SET_ZERO_ANCHOR]: (state: IReportState, action: PayloadAction<ISetZeroAnchorPayload>) => {
      const {
        index,
      } = action.payload as ISetZeroAnchorPayload;
      return getStateOnSetZeroAnchor(
        state,
        index,
      );
    },
    [ReportActionTypes.APPLY_DIMENSIONS]: (state:IReportState, action: PayloadAction<number>) => getStateOnApplyDimensions(state, action.payload),
    [ReportActionTypes.SET_MEASURE_STATE]: (state: IReportState, action: PayloadAction<ISetMeasureStatePayload>) => {
      const {
        sourceIndex, status,
      } = action.payload as ISetMeasureStatePayload;
      return getStateOnMeasureSetState(
        state,
        sourceIndex,
        status,
      );
    },
    [ReportActionTypes.REORDER_MEASURE]: (state:IReportState, action: PayloadAction<IReorderMeasurePayload>) => {
      const {
        sourceIndex, destinationIndex,
      } = action.payload as IReorderMeasurePayload;
      return getStateOnMeasureReorder(
        state,
        sourceIndex,
        destinationIndex,
      );
    },
    [ReportActionTypes.UPDATE_MEASURE]: (state: IReportState, action: PayloadAction<IUpdateMeasurePayload>) => {
      const { index, newMeasure } = action.payload as IUpdateMeasurePayload;
      return getStateOnMeasureUpdate(state, index, newMeasure);
    },
    [ReportActionTypes.TOGGLE_ZERO_ANCHORS]: (state:IReportState) => getStateOnToggleZeroAnchor(state),
    [ReportActionTypes.SEARCH_DROPDOWN_OPTIONS]: (state: IReportState, action: PayloadAction<ISearchDropdownOptionsPayload>) => {
      const {
        filterId, searchKey,
      } = action.payload as ISearchDropdownOptionsPayload;
      return getStateOnSearchDropdownOptions(
        state,
        filterId,
        searchKey,
      );
    },
    [ReportActionTypes.LOAD_MORE_DROPDOWN_OPTIONS]: (state: IReportState, action: PayloadAction<ILoadMoreDropdownOptionsPayload>) => {
      const {
        filterId,
      } = action.payload as ILoadMoreDropdownOptionsPayload;
      return getStateOnLoadMoreDropdownOptions(
        state,
        filterId,
      );
    },
    [ReportActionTypes.ADD_DYNAMIC_FILTER]: (state: IReportState, action: PayloadAction<IAddDynamicFilterPayload>) => {
      const {
        index,
      } = action.payload as IAddDynamicFilterPayload;
      return getStateOnAddDynamicFilter(
        state,
        index,
      );
    },
    [ReportActionTypes.DELETE_DYNAMIC_FILTER]: (state: IReportState, action: PayloadAction<IDeleteDynamicFilterPayload>) => {
      const {
        filterId,
      } = action.payload as IDeleteDynamicFilterPayload;
      return getStateOnDeleteDynamicFilter(
        state,
        filterId,
      );
    },
    [ReportActionTypes.RESET]: () => reportInitialState,
    [ReportActionTypes.SET_BINNING]: (state: IReportState, action: PayloadAction<ISetBinningPayload>) => {
      const { index, value } = action.payload as ISetBinningPayload;
      return getStateOnSetBinning(state, index, value);
    },
    [ReportActionTypes.SET_GROUPING_DRILLDOWN]: (state: IReportState, action: PayloadAction<ISetGroupingDrilldownPayload>) => {
      const {
        sourceIndex, status, destinationIndex, isSwapEnabled,
      } = action.payload as ISetGroupingDrilldownPayload;
      return getStateOnSetGroupingDrilldown(
        state,
        sourceIndex,
        status,
        destinationIndex,
        isSwapEnabled,
      );
    },
    [ReportActionTypes.UPDATE_TRIGGER_EXPORT]: (state: IReportState, action: PayloadAction<IUpdateTriggerExportPayload>) => {
      const {
        exportFormatType,
      } = action.payload as IUpdateTriggerExportPayload;
      state.triggerExport = exportFormatType;
    },
    [ReportActionTypes.SET_REPORT_INFO]: (state:IReportState, action: PayloadAction<ISetReportInfoPayload>) => {
      const { displayName, description, isSaveAs } = action.payload as ISetReportInfoPayload;
      if (isSaveAs) { // update name and description for saveas properties
        state.reportNameSaveAs = displayName;
        state.reportDescriptionSaveAs = description;
        state.isFormDisabled = getIsReportBuilderSaveFormDisable(displayName, description);
      } else {
        state.reportName = displayName;
        state.reportDescription = description;
      }
    },
    [ReportActionTypes.SET_VISUALIZATION]: (state:IReportState, action: PayloadAction<ISetVisualizationPayload>) => {
      const { type, attributes } = action.payload as ISetVisualizationPayload;
      const previousType = state.Visualization.type;
      state.Visualization.type = type;
      state.Visualization.attributes = attributes;
      state.enablesSaveAs = true;
      const appliedRowGroupDims = (state.appliedDimensions || [])
        .filter((d) => d.DimensionMode === DimensionMode.RowGroup && d.Applied !== DimensionStatus.NotApplied);
      if (type === VisualizationTypes.ComboChart) {
        if (!state.Visualization.Builder) state.Visualization.Builder = {} as any;
        const builder = state.Visualization.Builder as any;
        const hasExistingConfig = !!(builder.YAxis?.Columns?.length || builder.XAxis?.Columns?.length);
        if (!hasExistingConfig) {
          const COMBO_AXIS_LIMIT = 3;
          const measures = getAppliedMeasures(state.allMeasures).slice(0, COMBO_AXIS_LIMIT);
          builder.ComboConfig = measures.map((m, i) => ({
            columnId: m.Id,
            displayType: ComboDisplayType.Line,
            columnAlignment: ComboAxisAlignment.Auto,
            color: ChartDefaultcolors[i % ChartDefaultcolors.length],
          }));
          builder.YAxis = { Columns: measures.map((m) => m.Id) };
          builder.XAxis = { Columns: appliedRowGroupDims.slice(0, COMBO_AXIS_LIMIT).map((d) => d.Id) };
        } else {
          const appliedMeasureIds = new Set(getAppliedMeasures(state.allMeasures).map((m) => m.Id));
          const appliedRowGroupDimIds = new Set(appliedRowGroupDims.map((d) => d.Id));
          if (builder.YAxis?.Columns) {
            builder.YAxis.Columns = builder.YAxis.Columns.filter((id: string) => appliedMeasureIds.has(id));
          }
          if (builder.ComboConfig) {
            builder.ComboConfig = builder.ComboConfig.filter((c: any) => appliedMeasureIds.has(c.columnId));
          }
          if (builder.XAxis?.Columns) {
            builder.XAxis.Columns = builder.XAxis.Columns.filter((id: string) => appliedRowGroupDimIds.has(id));
          }
        }
      } else if (previousType === VisualizationTypes.ComboChart) {
        const builder = state.Visualization?.Builder as any;
        if (builder) {
          builder.ComboConfig = undefined;
          builder.XAxis = undefined;
          builder.YAxis = undefined;
        }
      }
    },
    [ReportActionTypes.SET_SHOW_REPORT]: (state:IReportState, action: PayloadAction<ISetShowReportPayload>) => {
      const { showReport } = action.payload as ISetShowReportPayload;
      return {
        ...state,
        showReport,
      };
    },
    [ReportActionTypes.DELETE_REPORT]: (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DELETE_REPORT] = APIRequestStatus.Processing;
    },
    [ReportActionTypes.SET_APPLY_CLICK_COUNT]: (state: IReportState, action: PayloadAction<number>) => {
      state.applyClickCount += action.payload;
    },
    [ReportActionTypes.SET_APPLY_CLICK_DRILLDOWN_COUNT]: (state: IReportState, action: PayloadAction<number>) => {
      state.applyClickCountDrilldown += action.payload;
    },
    [ReportActionTypes.SET_VISUALIZATION_SELECTION]: (state: IReportState, action: PayloadAction<ISetChartDataPayload>) => {
      const { type, attributeKey, attributes } = action.payload;
      state.selectedChartData.type = type;
      state.selectedChartData.attributeKey = attributeKey;
      state.selectedChartData.attributes = attributes;
    },
    [ReportActionTypes.SET_CONDITIONAL_FORMATTING]: (state: IReportState, action: PayloadAction<IConditionalFormatting>) => {
      const conditionalFormatting = { ...action.payload, Enabled: true };
      state.ConditionalFormatting = conditionalFormatting;
      state.reportConfig.Visualization.ConditionalFormatting = conditionalFormatting;
    },
    [ReportActionTypes.UPDATE_REPORTING_HIERARCHY]: (state: IReportState, action: PayloadAction<IHierarchy>) => {
      if (state.savedReportConfig?.Builder?.length > 0) {
        state.savedReportConfig.Builder = state.savedReportConfig.Builder.map((builder) => ({
          ...builder,
          Hierarchy: {
            ...builder.Hierarchy,
            ...action.payload,
          },
        }));
      }
    },
    [ReportActionTypes.SET_META_SELECTOR_OPTIONS]: (state:IReportState, action: PayloadAction<ISetMetaSelectorOptionsPayload>) => getStateOnSetEntityGroupingOptions(state, action.payload),
    [ReportActionTypes.APPLY_ENTITY_GROUPING]: (state:IReportState, action: PayloadAction<ISetDimensionsEntityGroupingPayload>) => getStateOnApplyEntityGrouping(state, action.payload),
    [ReportActionTypes.SET_ENTITY_GROUPING]: (state: IReportState, action: PayloadAction<ISetEntityGroupingPayload>) => getStateOnSetEntityGrouping(state, action.payload),
    [ReportActionTypes.RESET_LINKED_INSIGHT_DRILLDOWN]: (state: IReportState) => {
      state.linkedInsightDrilldown = { stateId: '', targetReportId: '' };
      state.requestProcessing[ReportActionTypes.SAVE_LINKED_INSIGHT_DRILLDOWN] = APIRequestStatus.AboutToLoad;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getReportConfig.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.REPORT_CONFIG_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getReportConfig.fulfilled, (state: IReportState, action: PayloadAction<SieraResponse<IReportConfig> & { drilldownStatePayload?: ObjModel.Obj | null }, string, ObjModel.Obj>) => {
      const { data, status, drilldownStatePayload } = action.payload;
      // Persist the temp drilldown state so it survives past this action and
      // is available at fetch-payload build time. See task-28 for the contract:
      // populated here on linked-insight load, consumed inside getUpdatedReportData,
      // cleared on getReportData.fulfilled after a write-through.
      state.drilldownStatePayload = drilldownStatePayload ?? null;
      state = getStateOnReportConfigLoadSuccess(state, data, action.meta.arg.variables, status);
      state.requestProcessing[ReportActionTypes.REPORT_CONFIG_LOAD_REQUEST] = APIRequestStatus.Success;
      state.reportId = action.meta.arg.reportId;
      // Fresh load — drop any stashed dashboard-drilldown wrapper values from a prior load.
      // Set explicitly: getStateOnReportConfigLoadSuccess mutates state in place and does
      // not clear this. The dashboard-drilldown write-through (AUTODRILLDOWN_V2) runs later
      // in the load chain, so this reset always precedes it.
      state.drilldownWrittenWrapperFilters = undefined;
    });
    builder.addCase(getReportConfig.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.REPORT_CONFIG_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getReportData.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Processing;
      state.error = undefined;
    });
    builder.addCase(getReportData.fulfilled, (state: IReportState, action: PayloadAction<IReportData>) => {
      state = getStateOnReportDataLoadSuccess(state, action.payload);
      state.requestProcessing[ReportActionTypes.REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
      state.error = undefined;

      // Linked-insight write-through + clear: on the first successful fetch after a
      // drilldown load, sticky the parent-mapped value onto appliedFilters/activeFilters
      // for each target custom filter so subsequent fetches (pagination, sort, export)
      // pick it up via the normal fan-out, then null out drilldownStatePayload so the
      // override block in getUpdatedReportData becomes a no-op going forward.
      if (state.drilldownStatePayload) {
        const drilldownFilters = state.drilldownStatePayload.Filters as ObjModel.Obj | undefined;
        const drilldownLinkedReportsConfig = state.drilldownStatePayload.LinkedReportsConfig as ILinkedConfig[] | undefined;
        if (drilldownFilters && isValidArrayAndNotEmpty(drilldownLinkedReportsConfig)) {
          drilldownLinkedReportsConfig.forEach((entry) => {
            if (entry?.LinkedReportId !== state.reportId) return;
            const filterMappings = entry?.LinkingConfig?.Filters;
            if (!filterMappings) return;
            Object.entries(filterMappings).forEach(([sourceFilterId, targetFilterId]) => {
              if (drilldownFilters[sourceFilterId] === undefined) return;
              const value = drilldownFilters[sourceFilterId];
              if (state.appliedFilters?.[targetFilterId]) {
                state.appliedFilters[targetFilterId].FilterResponse = value;
              }
              if (state.activeFilters?.[targetFilterId]) {
                state.activeFilters[targetFilterId].FilterResponse = value;
              }
            });
          });
        }
        state.drilldownStatePayload = null;
      }
    });
    builder.addCase(getReportData.rejected, (state: IReportState, action) => {
      state.requestProcessing[ReportActionTypes.REPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Failure;
      const payload = action.payload as { error: IPreviewDataError };
      state.error = payload.error as IPreviewDataError;
      state.drilldownStatePayload = null;
    });

    builder.addCase(getRawConfig.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.GET_RAW_CONFIG_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getRawConfig.fulfilled, (state: IReportState, action: PayloadAction<SieraResponse<any>>) => {
      const reportConfig = { ...action.payload.data, Columns: action?.payload?.data?.Columns?.filter((col: IColumn) => !(col?.Props?.Measure?.IsCustomExpression && col?.Props?.Mode === ColumnModes.NotApplied)) || [] };

      // to remove additional custom measures from config
      state.savedReportConfig = reportConfig;
      state.ConditionalFormatting = reportConfig.Visualization.ConditionalFormatting
        ? { ...reportConfig.Visualization.ConditionalFormatting, Enabled: true }
        : reportConfig.Visualization.ConditionalFormatting;
      // for list report, Builder is retrieved in get-raw-config api, rebuilding sort thereby
      const isReportListType = isListReport(action.payload.data?.Builder[0]?.Type);
      if (isReportListType) state.sort = getSort(reportConfig.Sorting, state.appliedDimensions, state.columns, isReportListType);

      // Resolve custom filter defaults from SubBuilder constituent filters.
      // The render config does not include SubBuilder filters, so custom filters
      // with only SubBuilder constituents are initialized with a fallback default.
      const resolvedDefaults = resolveSubBuilderCustomFilterDefaults(
        state.reportConfig?.CustomFilterDefinitions,
        reportConfig.SubBuilders,
      );
      Object.entries(resolvedDefaults).forEach(([cfId, defaultValue]) => {
        if (state.appliedFilters?.[cfId]) {
          state.appliedFilters[cfId].FilterResponse = defaultValue;
        }
        if (state.activeFilters?.[cfId]) {
          state.activeFilters[cfId].FilterResponse = defaultValue;
        }
        const cfEntry = state.filterConfig?.find((f) => f.ID === cfId);
        if (cfEntry) {
          cfEntry.DefaultValue = defaultValue;
        }
      });

      // Rebuild custom-filter wrappers with SubBuilders available so dropdown wrappers
      // whose FilterKey constituent lives in a subquery (not main Filters) pick up the
      // correct schema (LOS-ness, Metadata.FilterKey, LinkedTo). The initial
      // getReportConfig.fulfilled couldn't resolve these because SubBuilders aren't
      // in the render-config response. Swap only the wrapper entries — constituents
      // and standalone filters in filterConfig keep their already-loaded state.
      //
      // After patching the wrapper, also re-init its appliedFilters entry via
      // GetDefaultSingleFilter so shouldLoad flips back to true. The initial load
      // already fired the auto-loader against the wrong schema (Metadata.FilterKey
      // missing → lsqMultiSelectProcessor returned null without a network call →
      // shouldLoad set false). Re-initing makes the auto-loader's useEffect re-run
      // and fire GetLSQDropdownValues / losMultiSelectProcessor with the correct
      // schema this time.
      const definitions = state.reportConfig?.CustomFilterDefinitions;
      if (isValidArrayAndNotEmpty(definitions) && isValidArrayAndNotEmpty(reportConfig.SubBuilders) && isValidArrayAndNotEmpty(state.filterConfig)) {
        const rebuilt = buildCustomFiltersFromDefinitions(
          definitions,
          state.filterConfig,
          reportConfig.SubBuilders,
        );
        const cfIds = new Set(definitions.map((d) => d.ID));
        const rebuiltById = new Map(rebuilt.filter((f) => cfIds.has(f.ID)).map((w) => [w.ID, w] as const));
        state.filterConfig = state.filterConfig.map((f) => {
          if (!cfIds.has(f.ID)) return f;
          const r = rebuiltById.get(f.ID);
          return r ? { ...r, DefaultValue: f.DefaultValue } : f;
        });

        const fetchFilter = new FilterFetchUtility(null);
        state.filterConfig.forEach((f) => {
          if (!cfIds.has(f.ID)) return;
          let value: any = f.DefaultValue;
          if (f.Type === FilterType.UserMultiSelect) {
            value = {
              Type: f?.DropdownMetaData?.DropdownSelectType || UserFilterPayloadType.All,
              UserIds: f.DefaultValue,
            };
          }
          const refreshed = fetchFilter.GetDefaultSingleFilter(f, value);
          state.appliedFilters[f.ID] = { ...refreshed, label: f.Label };
          if (state.activeFilters?.[f.ID]) {
            state.activeFilters[f.ID] = state.appliedFilters[f.ID];
          }
          // Dashboard-drilldown override: the re-init above reset this wrapper to its
          // saved default to fix the sub-builder schema. If a drilldown wrote a dashboard
          // value for this wrapper, restore it on top of the corrected schema so the
          // dashboard selection survives (covers all-SubBuilder wrappers, whose value
          // get-report-config could not hydrate).
          const drilldownValue = state.drilldownWrittenWrapperFilters?.[f.ID];
          if (drilldownValue !== undefined) {
            state.appliedFilters[f.ID].FilterResponse = drilldownValue;
            if (state.activeFilters?.[f.ID]) {
              state.activeFilters[f.ID].FilterResponse = drilldownValue;
            }
          }
        });
      }

      state.requestProcessing[ReportActionTypes.GET_RAW_CONFIG_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getRawConfig.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.GET_RAW_CONFIG_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getDynamicDimensions.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_DIMENSIONS_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getDynamicDimensions.fulfilled, (state: IReportState, action: PayloadAction<IColumn[], string, any>) => {
      const selectedMeasure = action?.meta?.arg?.measureFieldInfo;
      state = getDynamicDimensionOnDynamicDimensionLoadSuccess(state, action.payload, selectedMeasure);
    });
    builder.addCase(getDynamicDimensions.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_DIMENSIONS_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getDynamicMeasures.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_MEASURES_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getDynamicMeasures.fulfilled, (state: IReportState, action: PayloadAction<DynamicMeasureResponse[]>) => {
      state = updateDynmaicMeasureStateOnSuccess(state, action.payload);
      state.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_MEASURES_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getDynamicMeasures.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.REPORT_DYNAMIC_MEASURES_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getFetchDrilldownReportV2.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.AUTODRILLDOWN_REPORTV2_DATA_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getFetchDrilldownReportV2.fulfilled, (state: IReportState, action: PayloadAction<any>) => {
      state = getStateOnAutodrilldownReportDataLoadSuccess(state, action.payload);
      state.requestProcessing[ReportActionTypes.AUTODRILLDOWN_REPORTV2_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getFetchDrilldownReportV2.rejected, (state: IReportState) => {
      state.autoDrilldownStateID = '';
      state.autoDrilldownDataV2 = null;
      state.requestProcessing[ReportActionTypes.AUTODRILLDOWN_REPORTV2_DATA_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getDataRefreshDetails.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DATA_REFRESH_DETAILS_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getDataRefreshDetails.fulfilled, (state: IReportState, action: PayloadAction<DataRefreshEntityTypes, string, ObjModel.Obj>) => {
      state = dataRefreshDetailsLoadSuccess(state, action.payload, action?.meta?.arg?.isPolling);
      state.requestProcessing[ReportActionTypes.DATA_REFRESH_DETAILS_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getDataRefreshDetails.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DATA_REFRESH_DETAILS_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getDataRefreshStatus.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DATA_REFRESH_STATUS_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getDataRefreshStatus.fulfilled, (state: IReportState, action: PayloadAction<IDataRefreshStatus, string, ObjModel.Obj>) => {
      state = dataRefreshStatusLoadSuccess(state, action.payload, action?.meta?.arg?.isPolling);
      state.requestProcessing[ReportActionTypes.DATA_REFRESH_STATUS_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getDataRefreshStatus.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DATA_REFRESH_STATUS_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(accesscontrol.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.ACCESS_CONTROL_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(accesscontrol.fulfilled, (state: IReportState, action: PayloadAction<Auth>) => {
      state.accessStatusCode = action.payload.statusCodes;
      state.requestProcessing[ReportActionTypes.ACCESS_CONTROL_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(accesscontrol.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.ACCESS_CONTROL_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getFiltersLoad.pending, (state: IReportState, action: PayloadAction<any, string, ObjModel.Obj>) => getStateOnFilterLoadRequest(state, action.meta.arg.filterId, action.meta.arg.variables, action.meta.arg.isLazyLoad));
    builder.addCase(getFiltersLoad.fulfilled, (state: IReportState, action: PayloadAction<any, string, ObjModel.Obj>) => getStateOnFilterLoadSuccess(state, action.meta.arg.filterId, action.payload));
    builder.addCase(getFiltersLoad.rejected, (state: IReportState, action: PayloadAction<any, string, ObjModel.Obj>) => getStateOnFilterLoadFailure(state, action.meta.arg.filterId));

    builder.addCase(deleteReport.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DELETE_REPORT] = APIRequestStatus.Processing;
    });
    builder.addCase(deleteReport.fulfilled, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DELETE_REPORT] = APIRequestStatus.Success;
    });
    builder.addCase(deleteReport.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DELETE_REPORT] = APIRequestStatus.Failure;
    });

    builder.addCase(saveReportConfig.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.SAVE_REPORT_CONFIG_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(saveReportConfig.fulfilled, (state: IReportState, action:PayloadAction<any, string, ObjModel.Obj>) => {
      const { isMarvin, isEmbedded } = action.meta.arg;
      window.open(getSaveSaveAsURL(isMarvin, isEmbedded, action.payload.data), '_self');
      state.requestProcessing[ReportActionTypes.SAVE_REPORT_CONFIG_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(saveReportConfig.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.SAVE_REPORT_CONFIG_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getExportLists.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_LIST_LOAD_REQUEST] = APIRequestStatus.Processing;
      state.exportListData = [];
    });
    builder.addCase(getExportLists.fulfilled, (state: IReportState, action:PayloadAction<IExportData[]>) => {
      state.requestProcessing[ReportActionTypes.EXPORT_LIST_LOAD_REQUEST] = APIRequestStatus.Success;
      state.exportListData = action.payload || [];
    });
    builder.addCase(getExportLists.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_LIST_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getExportUsageStatus.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_USAGE_STATUS_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getExportUsageStatus.fulfilled, (state: IReportState, action: PayloadAction<IExportUsageData>) => {
      const { TotalDailyExports, ExportsConsumed } = action.payload;
      state.requestProcessing[ReportActionTypes.EXPORT_USAGE_STATUS_REQUEST] = APIRequestStatus.Success;
      state.totalDailyExports = TotalDailyExports;
      state.exportsConsumed = ExportsConsumed;
      state.isDailyLimitReached = TotalDailyExports === ExportsConsumed;
    });
    builder.addCase(getExportUsageStatus.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_USAGE_STATUS_REQUEST] = APIRequestStatus.Failure;
    });
    builder.addCase(getExportListDataByIds.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_LIST_LOAD_BY_IDS_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getExportListDataByIds.fulfilled, (state: IReportState, action: PayloadAction<IExportData[]>) => {
      const exportList:IExportData[] = action.payload;
      const inProgressExportIds = isValidArrayAndNotEmpty(exportList) && exportList.map((item:IExportData) => item.ExportID);
      const currentExportListData = state.exportListData;
      const exportListDataUpdatedWithInprogressData = currentExportListData?.map((item:IExportData) => (inProgressExportIds.includes(item.ExportID) ? exportList?.find((i) => i.ExportID === item.ExportID) : item));
      state.exportListData = exportListDataUpdatedWithInprogressData;
      state.requestProcessing[ReportActionTypes.EXPORT_LIST_LOAD_BY_IDS_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getExportListDataByIds.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_LIST_LOAD_BY_IDS_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getRetryExportDatagetRetryExportData.pending, (state: IReportState, action: PayloadAction<string, string, ObjModel.Obj>) => {
      const newExportListData = state.exportListData;
      const indexOfReport = isValidArrayAndNotEmpty(newExportListData) && newExportListData.findIndex((rep:IExportData) => rep.ExportID === action.meta.arg);
      if (newExportListData[indexOfReport]) {
        newExportListData[indexOfReport].Status = ExportStatus.InProgress;
        state.exportListData = newExportListData;
        state.requestProcessing[ReportActionTypes.RETRY_EXPORT_REQUEST] = APIRequestStatus.Processing;
      }
    });
    builder.addCase(getRetryExportDatagetRetryExportData.fulfilled, (state: IReportState, action: PayloadAction<string, string, ObjModel.Obj>) => {
      const newExportListData = state.exportListData;
      const indexOfReport = isValidArrayAndNotEmpty(newExportListData) && newExportListData.findIndex((rep) => rep.ExportID === action.meta.arg.exportID);
      if (newExportListData[indexOfReport]) {
        newExportListData[indexOfReport].Status = ExportStatus.Failed;
      }
      state.exportListData = newExportListData;
      state.requestProcessing[ReportActionTypes.RETRY_EXPORT_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getRetryExportDatagetRetryExportData.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.RETRY_EXPORT_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getExportData.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getExportData.fulfilled, (state: IReportState, action: PayloadAction<IExportData>) => {
      const exportData = action.payload;
      if (isValidArray(state.exportListData)) {
        state.exportListData.push(exportData);
      }
      state.requestProcessing[ReportActionTypes.EXPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
      // since we added new export request to the queue we need to increment export consumed count
      state.exportsConsumed += 1;
      state.isDailyLimitReached = state.totalDailyExports === state.exportsConsumed;

      state.requestProcessing[ReportActionTypes.EXPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getExportData.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.EXPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getDynamicFilters.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DYNAMIC_FILTERS_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getDynamicFilters.fulfilled, (state: IReportState, action: PayloadAction<IFilterConfig[]>) => {
      state.dynamicFilters = action.payload || [];
      state.dynamicFilters = [...state.reportConfig?.Filters?.filter((item:IFilterConfig) => item.IsAdditional), ...(action.payload ?? [])];
      state.requestProcessing[ReportActionTypes.DYNAMIC_FILTERS_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getDynamicFilters.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DYNAMIC_FILTERS_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getExportDataV2.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DASHBOARD_REDIRECTED_EXPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Processing;
    });
    builder.addCase(getExportDataV2.fulfilled, (state: IReportState, action: PayloadAction<IExportData>) => {
      const exportData = action.payload;
      if (isValidArray(state.exportListData)) {
        state.exportListData.push(exportData);
      }
      // since we added new export request to the queue we need to increment export consumed count
      state.exportsConsumed += 1;
      state.isDailyLimitReached = state.totalDailyExports === state.exportsConsumed;

      state.requestProcessing[ReportActionTypes.DASHBOARD_REDIRECTED_EXPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Success;
    });
    builder.addCase(getExportDataV2.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.DASHBOARD_REDIRECTED_EXPORT_DATA_LOAD_REQUEST] = APIRequestStatus.Failure;
    });

    builder.addCase(getExpressionFields.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.GET_EXPRESSION_FIELDS] = APIRequestStatus.Processing;
    });
    builder.addCase(getExpressionFields.fulfilled, (state: IReportState, action: PayloadAction<Array<IField>, string, ObjModel.Obj>) => {
      state.fields = action.payload;
      state.requestProcessing[ReportActionTypes.GET_EXPRESSION_FIELDS] = APIRequestStatus.Success;
    });
    builder.addCase(getExpressionFields.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.GET_EXPRESSION_FIELDS] = APIRequestStatus.Failure;
    });

    builder.addCase(saveLinkedInsightDrilldown.pending, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.SAVE_LINKED_INSIGHT_DRILLDOWN] = APIRequestStatus.Processing;
    });
    builder.addCase(saveLinkedInsightDrilldown.fulfilled, (state: IReportState, action) => {
      state.requestProcessing[ReportActionTypes.SAVE_LINKED_INSIGHT_DRILLDOWN] = APIRequestStatus.Success;
      state.linkedInsightDrilldown.stateId = action.payload.stateId;
      state.linkedInsightDrilldown.targetReportId = action.payload.targetReportId;
    });
    builder.addCase(saveLinkedInsightDrilldown.rejected, (state: IReportState) => {
      state.requestProcessing[ReportActionTypes.SAVE_LINKED_INSIGHT_DRILLDOWN] = APIRequestStatus.Failure;
    });
  },
});

export const {
  [ReportActionTypes.AUTODRILLDOWN]: reportAutodrilldown,
  [ReportActionTypes.AUTODRILLDOWN_V2]: getTempAutodrilldownStateId,
  [ReportActionTypes.APPLY_FILTERS]: applyReportFilters,
  [ReportActionTypes.PAGINATION_CHANGE]: reportPaginationChange,
  [ReportActionTypes.SORTING_CHANGE]: reportSortingChange,
  [ReportActionTypes.CHANGE_PIVOT_SORTING]: reportPivotSortChange,
  [ReportActionTypes.REVERSE_DRILLDOWN]: reportReverseDrilldown,
  [ReportActionTypes.RESET_SHOW_OTHERS_COLUMN_FLAG]: reportResetOthersColumn,
  [ReportActionTypes.SET_GROUPING]: reportSetGrouping,
  [ReportActionTypes.SET_ZERO_ANCHOR]: reportSetZeroAnchor,
  [ReportActionTypes.APPLY_DIMENSIONS]: reportApplyDimensions,
  [ReportActionTypes.SET_MEASURE_STATE]: reportSetMeasureState,
  [ReportActionTypes.REORDER_MEASURE]: reportReorderMeasure,
  [ReportActionTypes.UPDATE_MEASURE]: reportUpdateMeasure,
  [ReportActionTypes.TOGGLE_ZERO_ANCHORS]: reportToggleZeroAnchor,
  [ReportActionTypes.SEARCH_DROPDOWN_OPTIONS]: reportSearchDropdownOptions,
  [ReportActionTypes.LOAD_MORE_DROPDOWN_OPTIONS]: reportLoadMoreOptions,
  [ReportActionTypes.ADD_DYNAMIC_FILTER]: reportAddDynamicFilter,
  [ReportActionTypes.DELETE_DYNAMIC_FILTER]: removeDynamicFilter,
  [ReportActionTypes.RESET]: reportReset,
  [ReportActionTypes.SET_BINNING]: reportSetBinning,
  [ReportActionTypes.SET_GROUPING_DRILLDOWN]: reportSetGroupingDrilldown,
  [ReportActionTypes.UPDATE_TRIGGER_EXPORT]: reportUpdateTriggerExport,
  [ReportActionTypes.SET_REPORT_INFO]: setReportDetails,
  [ReportActionTypes.SET_VISUALIZATION]: reportSetVisualization,
  [ReportActionTypes.SET_CONDITIONAL_FORMATTING]: reportSetConditionalFormatting,
  [ReportActionTypes.SET_SHOW_REPORT]: setShowReport,
  [ReportActionTypes.DRILLDOWN_CHANGE]: reportDrilldownChange,
  [ReportActionTypes.DRILLDOWN_CHANGE_FOR_CUSTOM_HIERARCHY]: reportDrilldownChangeForCustomHierarchy,
  [ReportActionTypes.SET_TOOLBAR_MENU_ITEM]: reportSetToolBarMenuItem,
  [ReportActionTypes.FILTER_CHANGE]: reportFilterChange,
  [ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_REQUEST]: loadAutodrilldown,
  [ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_SUCCESS]: autodrilldownLoadSuccess,
  [ReportActionTypes.AUTODRILLDOWN_REPORT_DATA_LOAD_FAILURE]: autodrilldownLoadFailure,
  [ReportActionTypes.REORDER_COLUMNS]: reorderColumns,
  [ReportActionTypes.DRILLDOWN_SORTING_CHANGE]: drilldownSortingChange,
  [ReportActionTypes.DELETE_REPORT]: resetDeleteReportRequestStatus,
  [ReportActionTypes.SET_APPLY_CLICK_COUNT]: setApplyClickCount,
  [ReportActionTypes.SET_APPLY_CLICK_DRILLDOWN_COUNT]: setApplyClickCountDrilldown,
  [ReportActionTypes.SET_VISUALIZATION_SELECTION]: setSelectedChartData,
  [ReportActionTypes.UPDATE_REPORTING_HIERARCHY]: updateReportingHierarchy,
  [ReportActionTypes.SET_META_SELECTOR_OPTIONS]: reportSetEntityGroupingOptions,
  [ReportActionTypes.APPLY_ENTITY_GROUPING]: reportApplyEntityGrouping,
  [ReportActionTypes.SET_ENTITY_GROUPING]: reportSetEntityGrouping,
  [ReportActionTypes.RESET_LINKED_INSIGHT_DRILLDOWN]: resetLinkedInsightDrilldown,
} = reportStoreSlice.actions;

export default reportStoreSlice;
